import { Button, Card, Grid, Group, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { productsRangeToExport } from '../../constants/exportProductsRange';
import ErrorMessage from '../ErrorMessage';
import ConfirmationModal from '../Modals/ConfirmationModal';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import SegmentSelector from '../Segments/SegmentSelector';

import LabeledSlider from '../LabeledSlider';
import LockableButton from '../Lockable/LockableButton';
import Spinner from '../Spinner';

const FacebookProductSetForm = ({
  facebookProductSet: passedFacebookProductSet,
  segments,
  saveFacebookProductSet,
  deleteFacebookProductSet,
}) => {
  const navigate = useNavigate();

  const [facebookProductSet, setFacebookProductSet] = useState({
    ...passedFacebookProductSet,
  });
  const [userError, setUserError] = useState();
  const [selectedProductFeedScope, setSelectedProductFeedScope] = useState(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { accountId, domainId } = useParams();

  if (facebookProductSet === undefined || segments === undefined) {
    return <Spinner />;
  }

  if (facebookProductSet === null) {
    return <ErrorMessage message="Facebook product set does not exist!" />;
  }

  const handleChange = (e) => {
    setFacebookProductSet({ ...facebookProductSet, [e.target.name]: e.target.value });
  };

  let deleteButton;
  if (deleteFacebookProductSet) {
    deleteButton = (
      <LockableButton restrictionKey="facebookProductSetsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  return (
    <>
      <Card withBorder>
        <Grid>
          <Grid.Col span={4}>
            <Text fw={500}>Name*</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TextInput
              name="name"
              type="text"
              value={facebookProductSet.name}
              placeholder="Test Facebook Product Set"
              onChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mt="md">
          <Grid.Col span={4}>
            <Text fw={500}>Meta product set ID</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            {facebookProductSet.facebookProductSetId ? (
              <Text>{facebookProductSet.facebookProductSetId}</Text>
            ) : (
              <Text c="dimmed">Not created on Meta yet</Text>
            )}
          </Grid.Col>
        </Grid>
        <Grid mt="md">
          <Grid.Col span={4}>
            <Text fw={500}>Type*</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <SegmentedControl
              value={facebookProductSet.type ?? ''}
              onChange={(type) => setFacebookProductSet({ ...facebookProductSet, type })}
              data={[
                { label: 'Full', value: 'full' },
                { label: 'Ranked subset', value: 'ranked' },
              ]}
            />
          </Grid.Col>
        </Grid>
        {facebookProductSet.type === 'ranked' && (
          <>
            <Grid mt="md">
              <Grid.Col span={4}>
                <Text fw={500}>Product Feed</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <ProductFeedSelector
                  accountId={accountId}
                  domainId={domainId}
                  productFeedId={parseInt(facebookProductSet.productFeedId)}
                  onSelect={(productFeedId, productFeed) => {
                    setFacebookProductSet({ ...facebookProductSet, productFeedId });
                    setSelectedProductFeedScope(productFeed?.scope);
                  }}
                  onInitialLoad={(productFeed) => setSelectedProductFeedScope(productFeed?.scope)}
                  forType="facebook product set"
                  scope={['marketingChannels', 'ads']}
                />
              </Grid.Col>
            </Grid>

            {selectedProductFeedScope !== 'ads' && (
              <SegmentSelector
                title="Ranking Segment"
                description="Products will be ranked and filtered based on user preferences from this segment."
                segmentId={facebookProductSet.segmentId}
                onSelect={(segment) => handleChange({ target: { name: 'segmentId', value: segment?.id } })}
                campaignScope="transactional"
                scope="behavior"
              />
            )}
          </>
        )}

        {facebookProductSet.type !== 'full' && (
          <Grid mt="md">
            <Grid.Col span={4}>
              <Text fw={500}>Max products to synchronise</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <LabeledSlider
                min={productsRangeToExport.min}
                max={productsRangeToExport.max}
                value={facebookProductSet.count}
                onChange={(count) => setFacebookProductSet({ ...facebookProductSet, count })}
              />
              <Text size="sm" c="dimmed">
                Full product sets includes all products from your catalog. Ranked subset product sets include a subset
                of your catalog, optimized for a specific segment.
              </Text>
            </Grid.Col>
          </Grid>
        )}
      </Card>

      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={() => navigate(-1)}>
          Back
        </Button>
        {deleteButton}
        <LockableButton
          restrictionKey={`facebookProductSets${facebookProductSet.id ? 'Update' : 'Create'}`}
          disabled={
            facebookProductSet.type === 'ranked' &&
            (!facebookProductSet.productFeedId || (selectedProductFeedScope !== 'ads' && !facebookProductSet.segmentId))
          }
          variant="primary"
          onClick={() => saveFacebookProductSet(facebookProductSet).catch(setUserError)}
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
      </Group>
      {userError && (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      )}
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        error={userError}
        confirm={(cb) => deleteFacebookProductSet(cb).catch(setUserError)}
        message="This action cannot be undone!"
      />
    </>
  );
};

export default FacebookProductSetForm;
